import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Plus, Trash2, Edit3, X, PackagePlus, Receipt, FileText, Search, Upload, Download, RefreshCw, AlertCircle, Paperclip } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CURRENCIES = ["RON", "EUR", "USD"];
const DOC_TYPES = [
  { value: "factura", label: "Factură" },
  { value: "aviz", label: "Aviz" },
];

const emptyItem = () => ({ sku: "", name: "", quantity: 1, unit_price: 0 });
const todayISO = () => new Date().toISOString().slice(0, 10);
const emptyForm = () => ({
  supplier: "",
  document_date: todayISO(),
  stock_date: todayISO(),
  document_type: "factura",
  document_number: "",
  total_value: 0,
  currency: "RON",
  exchange_rate: 0,
  items: [emptyItem()],
  notes: "",
  customs_vat: 0,
});

function fmt(n, c = "RON") {
  const v = Number(n) || 0;
  return `${v.toFixed(2)} ${c}`;
}

// ---------- Autocomplete cells (shared catalog of {sku, name}) ----------
function AutocompleteInput({ testid, value, placeholder, onChange, onPick, suggestions, renderItem }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  useEffect(() => { setActive(0); }, [suggestions.length, value]);
  const close = () => setTimeout(() => setOpen(false), 120); // allow click to fire
  const onKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const p = suggestions[active];
      if (p) { onPick(p); setOpen(false); }
    } else if (e.key === "Escape") { setOpen(false); }
  };
  return (
    <div className="relative">
      <input
        data-testid={testid}
        value={value}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={close}
        onKeyDown={onKey}
        autoComplete="off"
        className="w-full rounded border border-zinc-800 bg-zinc-950 px-2 py-1 text-xs text-zinc-100"
      />
      {open && suggestions.length > 0 && (
        <div
          data-testid={`${testid}-dropdown`}
          className="absolute left-0 right-0 top-full z-40 mt-1 max-h-64 overflow-y-auto rounded-md border border-zinc-800 bg-zinc-950 shadow-2xl"
        >
          {suggestions.map((p, i) => (
            <button
              type="button"
              key={p.sku}
              data-testid={`${testid}-opt-${i}`}
              onMouseDown={(e) => { e.preventDefault(); onPick(p); setOpen(false); }}
              onMouseEnter={() => setActive(i)}
              className={`block w-full px-2 py-1.5 text-left text-[11px] ${i === active ? "bg-orange-500/15 text-orange-100" : "text-zinc-200 hover:bg-zinc-900"}`}
            >
              {renderItem(p)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SkuAutocomplete({ idx, value, catalog, onChange, onPick }) {
  const q = (value || "").trim().toLowerCase();
  const suggestions = q
    ? catalog.filter((p) => p.sku.toLowerCase().includes(q)).slice(0, 20)
    : catalog.slice(0, 20);
  return (
    <AutocompleteInput
      testid={`item-sku-${idx}`}
      value={value}
      placeholder="BRAG-101"
      onChange={(v) => {
        onChange(v);
        // exact match → auto-fill name
        const exact = catalog.find((p) => p.sku.toLowerCase() === v.trim().toLowerCase());
        if (exact) onPick(exact);
      }}
      onPick={onPick}
      suggestions={suggestions}
      renderItem={(p) => (
        <>
          <span className="font-mono font-semibold text-orange-300">{p.sku}</span>
          <span className="ml-2 text-zinc-400">{(p.name || "").slice(0, 80)}</span>
        </>
      )}
    />
  );
}

function NameAutocomplete({ idx, value, catalog, onChange, onPick }) {
  const q = (value || "").trim().toLowerCase();
  const suggestions = q.length >= 2
    ? catalog.filter((p) => (p.name || "").toLowerCase().includes(q)).slice(0, 20)
    : [];
  return (
    <AutocompleteInput
      testid={`item-name-${idx}`}
      value={value}
      placeholder="Denumire opțională (tastează pt sugestii)"
      onChange={onChange}
      onPick={onPick}
      suggestions={suggestions}
      renderItem={(p) => (
        <>
          <span className="font-mono text-orange-300">{p.sku}</span>
          <span className="ml-2 text-zinc-200">{(p.name || "").slice(0, 80)}</span>
        </>
      )}
    />
  );
}

// ---------- Documents Section (attached files per import) ----------
function ImportDocuments({ importId }) {
  // Up to 10 files per upload request, ≤50 MB each. Accepts any binary
  // (PDF, image, ZIP archive) so the user can bulk-upload a few invoices
  // and a zipped pack of receipts in one go.
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!importId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/imports/${importId}/documents`);
      setDocs(r.data?.documents || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [importId]);

  useEffect(() => { refresh(); }, [refresh]);

  const fmtSize = (n) => {
    if (!n) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };
  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" }); }
    catch { return iso; }
  };

  const onPickFiles = () => inputRef.current?.click();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (files.length > 10) {
      setError("Maxim 10 fișiere per cerere");
      e.target.value = "";
      return;
    }
    setUploading(true);
    setError("");
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      await axios.post(`${API}/imports/${importId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDelete = (doc) => async () => {
    if (!window.confirm(`Ștergi documentul „${doc.filename}"?`)) return;
    setDeletingId(doc.id);
    setError("");
    try {
      await axios.delete(`${API}/imports/${importId}/documents/${doc.id}`);
      setDocs((arr) => arr.filter((d) => d.id !== doc.id));
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const iconForType = (ctype) => {
    const c = (ctype || "").toLowerCase();
    if (c.includes("zip") || c.includes("archive")) return "📦";
    if (c.startsWith("image/")) return "🖼️";
    if (c.includes("pdf")) return "📄";
    return "📎";
  };

  return (
    <div data-testid="import-documents">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
          <Paperclip className="h-3 w-3" /> Documente atașate {docs.length > 0 && <span className="text-zinc-400 normal-case">({docs.length})</span>}
        </h4>
        <button
          type="button" onClick={onPickFiles} disabled={uploading}
          data-testid="import-doc-upload"
          className="inline-flex items-center gap-1.5 rounded-md border border-orange-500/50 bg-orange-500/10 px-3 py-1 text-xs text-orange-200 hover:bg-orange-500/20 disabled:opacity-50"
        >
          {uploading ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
          {uploading ? "Se încarcă..." : "Încarcă fișiere"}
        </button>
        <input
          ref={inputRef} type="file" multiple
          accept=".pdf,.zip,.png,.jpg,.jpeg,.webp,.heic,application/pdf,application/zip,image/*"
          className="hidden" onChange={onFiles}
          data-testid="import-doc-input"
        />
      </div>

      {error && (
        <div className="mb-2 flex items-start gap-2 rounded-md border border-rose-500/40 bg-rose-500/10 px-2.5 py-1.5 text-[11px] text-rose-200">
          <AlertCircle className="mt-0.5 h-3 w-3" /> {error}
        </div>
      )}

      {loading ? (
        <p className="text-xs text-zinc-500">Se încarcă...</p>
      ) : docs.length === 0 ? (
        <button
          type="button" onClick={onPickFiles} disabled={uploading}
          data-testid="import-doc-empty-btn"
          className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-zinc-700 bg-zinc-900/40 px-3 py-4 text-xs text-zinc-400 hover:border-orange-500/50 hover:text-orange-200"
        >
          <Upload className="h-3.5 w-3.5" /> Selectează PDF / imagine / ZIP (până la 10 fișiere, 50 MB / fișier)
        </button>
      ) : (
        <ul className="space-y-1.5">
          {docs.map((d) => (
            <li key={d.id} data-testid={`import-doc-${d.id}`}
                className="flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-950/60 px-2.5 py-1.5">
              <span className="text-base leading-none">{iconForType(d.content_type)}</span>
              <div className="flex-1 min-w-0">
                <p className="truncate text-xs text-zinc-200" title={d.filename}>{d.filename}</p>
                <p className="text-[10px] text-zinc-500">{fmtSize(d.size)} · {fmtDate(d.uploaded_at)} · {d.uploaded_by || "—"}</p>
              </div>
              <a
                href={`${BACKEND_URL}/api/imports/${importId}/documents/${d.id}`}
                target="_blank" rel="noreferrer"
                data-testid={`import-doc-open-${d.id}`}
                className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-1 text-[11px] text-sky-200 hover:bg-sky-500/20"
              >
                <Download className="h-3 w-3" /> Deschide
              </a>
              <button
                type="button" onClick={onDelete(d)} disabled={deletingId === d.id}
                data-testid={`import-doc-delete-${d.id}`}
                className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-[11px] text-rose-200 hover:bg-rose-500/20 disabled:opacity-50"
              >
                {deletingId === d.id ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- Builder Modal ----------
function ImportFormModal({ open, editing, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [catalog, setCatalog] = useState([]); // [{sku, name}] deduped

  useEffect(() => {
    if (!open) return;
    // Load product catalog once per modal open. Lightweight endpoint returns
    // just {sku, name} deduped by SKU.
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/products/catalog`);
        if (cancelled) return;
        const list = (r.data?.items || []).map((p) => ({ sku: p.sku, name: p.name || "" }));
        setCatalog(list);
      } catch (_e) { /* autocomplete is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        ...emptyForm(),
        ...editing,
        items: (editing.items && editing.items.length ? editing.items : [emptyItem()]).map((i) => ({ ...i })),
        exchange_rate: editing.exchange_rate || 0,
      });
    } else {
      setForm(emptyForm());
    }
    setErr("");
  }, [open, editing]);

  if (!open) return null;

  const needsExchangeRate = form.currency !== "RON";
  const itemsSum = (form.items || []).reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (idx, patch) => setForm((f) => ({
    ...f,
    items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
  }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (idx) => setForm((f) => ({
    ...f,
    items: f.items.length > 1 ? f.items.filter((_, i) => i !== idx) : f.items,
  }));

  const submit = async () => {
    setErr("");
    if (!form.supplier.trim()) return setErr("Furnizorul este obligatoriu.");
    if (!form.document_number.trim()) return setErr("Numărul documentului este obligatoriu.");
    if (needsExchangeRate && !(Number(form.exchange_rate) > 0)) {
      return setErr("Cursul de schimb este obligatoriu pentru EUR/USD.");
    }
    const cleanItems = form.items.filter((i) => i.sku.trim() && Number(i.quantity) > 0);
    if (cleanItems.length === 0) return setErr("Adaugă cel puțin un produs cu SKU + cantitate.");
    const payload = {
      ...form,
      total_value: Number(form.total_value) || 0,
      exchange_rate: needsExchangeRate ? Number(form.exchange_rate) : null,
      customs_vat: Number(form.customs_vat) > 0 ? Number(form.customs_vat) : 0,
      items: cleanItems.map((it) => ({
        sku: it.sku.trim(),
        name: it.name.trim(),
        quantity: Number(it.quantity),
        unit_price: Number(it.unit_price) || 0,
      })),
    };
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/imports/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/imports`, payload);
      }
      onSaved?.();
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <div className="w-full max-w-4xl max-h-[92vh] flex flex-col rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="import-modal">
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Import</div>
            <h3 className="text-sm font-semibold text-zinc-100">{editing ? "Editează import" : "Adaugă import nou"}</h3>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200" data-testid="import-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* ---- HEADER FIELDS ---- */}
          <div>
            <h4 className="mb-2 text-[10px] uppercase tracking-wider text-zinc-500">Date document</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="lg:col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Furnizor</label>
                <input data-testid="import-supplier" value={form.supplier} onChange={(e) => setField("supplier", e.target.value)}
                  placeholder="Ex: SC Furnizor SRL"
                  className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Tip document</label>
                <select data-testid="import-doc-type" value={form.document_type} onChange={(e) => setField("document_type", e.target.value)}
                  className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100">
                  {DOC_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Nr document</label>
                <input data-testid="import-doc-number" value={form.document_number} onChange={(e) => setField("document_number", e.target.value)}
                  placeholder="Ex: 12345"
                  className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Data document</label>
                <input data-testid="import-doc-date" type="date" value={form.document_date} onChange={(e) => setField("document_date", e.target.value)}
                  className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Data în stoc</label>
                <input data-testid="import-stock-date" type="date" value={form.stock_date} onChange={(e) => setField("stock_date", e.target.value)}
                  className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100" />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Valoare totală</label>
                <input data-testid="import-total" type="number" step="0.01" value={form.total_value} onChange={(e) => setField("total_value", e.target.value)}
                  className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Moneda</label>
                <select data-testid="import-currency" value={form.currency} onChange={(e) => setField("currency", e.target.value)}
                  className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100">
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {needsExchangeRate && (
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500">Curs schimb (1 {form.currency} = ? LEI)</label>
                  <input data-testid="import-exchange-rate" type="number" step="0.0001" value={form.exchange_rate} onChange={(e) => setField("exchange_rate", e.target.value)}
                    placeholder="Ex: 4.9750"
                    className="mt-1 w-full rounded-md border border-amber-500/40 bg-zinc-950 px-3 py-2 text-sm text-zinc-100" />
                </div>
              )}
            </div>
            {needsExchangeRate && Number(form.exchange_rate) > 0 && Number(form.total_value) > 0 && (
              <div className="mt-2 text-xs text-zinc-400">
                Echivalent: <span className="font-medium text-emerald-300">{fmt(Number(form.total_value) * Number(form.exchange_rate), "RON")}</span>
              </div>
            )}
          </div>

          {/* ---- PRODUCT ITEMS ---- */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-[10px] uppercase tracking-wider text-zinc-500">Produse</h4>
              <button data-testid="import-add-item" onClick={addItem}
                className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-xs font-medium text-emerald-200 hover:bg-emerald-500/20">
                <Plus className="h-3 w-3" /> Adaugă produs
              </button>
            </div>
            <div className="overflow-x-auto rounded-lg border border-zinc-800">
              <table className="w-full text-xs">
                <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="px-3 py-2 text-left w-32">SKU</th>
                    <th className="px-3 py-2 text-left">Nume produs</th>
                    <th className="px-3 py-2 text-right w-24">Cant.</th>
                    <th className="px-3 py-2 text-right w-32">Preț ({form.currency})</th>
                    <th className="px-3 py-2 text-right w-32">Subtotal</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((it, idx) => (
                    <tr key={idx} className="border-t border-zinc-900">
                      <td className="px-2 py-1">
                        <SkuAutocomplete
                          idx={idx}
                          value={it.sku}
                          catalog={catalog}
                          onChange={(sku) => setItem(idx, { sku })}
                          onPick={(p) => setItem(idx, { sku: p.sku, name: p.name })}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <NameAutocomplete
                          idx={idx}
                          value={it.name}
                          catalog={catalog}
                          onChange={(name) => setItem(idx, { name })}
                          onPick={(p) => setItem(idx, { sku: p.sku, name: p.name })}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input data-testid={`item-qty-${idx}`} type="number" step="0.01" value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })}
                          className="w-full rounded border border-zinc-800 bg-zinc-950 px-2 py-1 text-xs text-right text-zinc-100" />
                      </td>
                      <td className="px-2 py-1">
                        <input data-testid={`item-price-${idx}`} type="number" step="0.0001" value={it.unit_price} onChange={(e) => setItem(idx, { unit_price: e.target.value })}
                          className="w-full rounded border border-zinc-800 bg-zinc-950 px-2 py-1 text-xs text-right text-zinc-100" />
                      </td>
                      <td className="px-3 py-1 text-right text-zinc-300">
                        {fmt((Number(it.quantity) || 0) * (Number(it.unit_price) || 0), form.currency)}
                      </td>
                      <td className="px-2 py-1 text-right">
                        <button onClick={() => removeItem(idx)} disabled={form.items.length === 1}
                          className="rounded p-1 text-zinc-500 hover:bg-rose-500/10 hover:text-rose-400 disabled:opacity-40">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t border-zinc-800 bg-zinc-900/40">
                    <td colSpan={4} className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-zinc-500">Total linii</td>
                    <td className="px-3 py-2 text-right text-sm font-semibold text-emerald-300">{fmt(itemsSum, form.currency)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
            {Math.abs(itemsSum - Number(form.total_value)) > 0.5 && Number(form.total_value) > 0 && (
              <p className="mt-1 text-[11px] text-amber-300">
                Atenție: suma liniilor ({fmt(itemsSum, form.currency)}) diferă de valoarea documentului ({fmt(form.total_value, form.currency)}).
              </p>
            )}
          </div>

          {/* ---- TVA ACHITAT ÎN VAMĂ ---- */}
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4">
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-[10px] uppercase tracking-wider text-emerald-300">TVA achitat în vamă</h4>
              <span className="text-[10px] text-zinc-500">se înregistrează automat în Financiar</span>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500">Valoare TVA (RON)</label>
                <input
                  data-testid="import-customs-vat"
                  type="number" step="0.01" min="0"
                  value={form.customs_vat || ""}
                  onChange={(e) => setField("customs_vat", e.target.value)}
                  placeholder="0.00"
                  className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-emerald-500/50 focus:outline-none" />
              </div>
              <div className="flex items-end">
                <p className="text-[11px] text-zinc-400">
                  Suma introdusă aici va apărea automat în <span className="font-medium text-emerald-300">Financiar → TVA plătit RO</span> ca factură <span className="font-mono text-zinc-300">„Import nr. {form.document_number || "—"} din {form.document_date}&rdquo;</span> pentru luna importului. Setează 0 pentru a o elimina.
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Notițe</label>
            <textarea data-testid="import-notes" rows={2} value={form.notes || ""} onChange={(e) => setField("notes", e.target.value)}
              className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100" />
          </div>

          {editing?.id ? (
            <ImportDocuments importId={editing.id} />
          ) : (
            <div className="rounded-md border border-dashed border-zinc-800 bg-zinc-950/40 px-3 py-3 text-[11px] text-zinc-500" data-testid="import-docs-hint">
              <Paperclip className="mr-1 inline h-3 w-3" /> Documentele atașate (PDF, scanări, ZIP) pot fi încărcate după salvarea importului — redeschide-l din listă.
            </div>
          )}

          {err && (
            <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200" data-testid="import-error">
              {err}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-zinc-800 px-5 py-3">
          <button onClick={onClose} className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:border-zinc-700">
            Anulează
          </button>
          <button data-testid="import-save" onClick={submit} disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-md bg-orange-500 px-4 py-1.5 text-xs font-medium text-white hover:bg-orange-400 disabled:opacity-60">
            {saving ? "Se salvează..." : (editing ? "Salvează modificările" : "Salvează importul")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Main Page ----------
export default function Imports() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [stats, setStats] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/imports`, { params: { page, per_page: perPage, supplier: search || undefined } });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search]);

  const fetchStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/imports/stats`);
      setStats(r.data);
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => { fetchAll(); fetchStats(); }, [fetchAll, fetchStats]);

  const handleDelete = async (it) => {
    if (!window.confirm(`Șterg importul ${it.document_number} de la ${it.supplier}?`)) return;
    await axios.delete(`${API}/imports/${it.id}`);
    fetchAll(); fetchStats();
  };

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8" data-testid="imports-page">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-[11px] text-zinc-500">Panou de control / <span className="text-zinc-300">Importuri</span></div>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-50">Importuri furnizori</h1>
          <p className="text-sm text-zinc-500">Înregistrează facturi și avize de la furnizori, cu produse și prețuri de achiziție.</p>
        </div>
        <button data-testid="import-new" onClick={() => { setEditing(null); setOpenModal(true); }}
          className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-400">
          <Plus className="h-4 w-4" /> Import nou
        </button>
      </div>

      {stats && (
        <div className="mb-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Total importuri</div>
            <div className="mt-1 text-xl font-semibold text-zinc-100">{stats.total_imports}</div>
          </div>
          <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Valoare totală (RON)</div>
            <div className="mt-1 text-xl font-semibold text-emerald-300">{fmt(stats.ron_total, "RON")}</div>
          </div>
          {(stats.top_suppliers || []).slice(0, 2).map((s, i) => (
            <div key={i} className="rounded-xl border border-zinc-800 bg-zinc-950/60 px-4 py-3">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 truncate" title={s.supplier}>{s.supplier}</div>
              <div className="mt-1 text-xl font-semibold text-zinc-100">{fmt(s.ron, "RON")}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
          <input data-testid="import-search" placeholder="Caută furnizor..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full rounded-lg border border-zinc-800 bg-zinc-950 pl-9 pr-3 py-2 text-sm text-zinc-100" />
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-zinc-800">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-3 py-2 text-left">Furnizor</th>
              <th className="px-3 py-2 text-left">Tip</th>
              <th className="px-3 py-2 text-left">Nr / Data</th>
              <th className="px-3 py-2 text-left">Data stoc</th>
              <th className="px-3 py-2 text-right">Valoare</th>
              <th className="px-3 py-2 text-right">RON</th>
              <th className="px-3 py-2 text-right">Produse</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-xs text-zinc-500">Se încarcă...</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-12 text-center text-xs text-zinc-500">
                <PackagePlus className="mx-auto mb-2 h-6 w-6 text-zinc-700" />
                Niciun import înregistrat. Apasă &quot;Import nou&quot; sus.
              </td></tr>
            )}
            {!loading && items.map((it) => (
              <tr key={it.id} className="border-t border-zinc-900 hover:bg-zinc-900/40" data-testid={`import-row-${it.id}`}>
                <td className="px-3 py-2 text-zinc-100">{it.supplier}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${it.document_type === "factura" ? "bg-sky-500/15 text-sky-200" : "bg-amber-500/15 text-amber-200"}`}>
                    {it.document_type === "factura" ? <Receipt className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
                    {it.document_type === "factura" ? "Factură" : "Aviz"}
                  </span>
                </td>
                <td className="px-3 py-2 text-zinc-300">
                  <div className="font-medium text-zinc-100">{it.document_number}</div>
                  <div className="text-[10px] text-zinc-500">{it.document_date}</div>
                </td>
                <td className="px-3 py-2 text-zinc-400 text-xs">{it.stock_date}</td>
                <td className="px-3 py-2 text-right">
                  {fmt(it.total_value, it.currency)}
                  {it.currency !== "RON" && it.exchange_rate ? (
                    <div className="text-[10px] text-zinc-500">curs: {Number(it.exchange_rate).toFixed(4)}</div>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-right text-emerald-300">{fmt(it.total_value_ron, "RON")}</td>
                <td className="px-3 py-2 text-right text-zinc-400 text-xs">
                  <div className="inline-flex items-center gap-1">
                    <span>{(it.items || []).length}</span>
                    {(it.documents || []).length > 0 && (
                      <span
                        title={`${it.documents.length} document${it.documents.length === 1 ? "" : "e"} atașat${it.documents.length === 1 ? "" : "e"}`}
                        data-testid={`import-docs-badge-${it.id}`}
                        className="inline-flex items-center gap-0.5 rounded bg-sky-500/15 px-1 py-0.5 text-[9px] font-semibold text-sky-200"
                      >
                        <Paperclip className="h-2.5 w-2.5" /> {(it.documents || []).length}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => { setEditing(it); setOpenModal(true); }}
                      className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200" title="Editează" data-testid={`import-edit-${it.id}`}>
                      <Edit3 className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => handleDelete(it)}
                      className="rounded-md p-1.5 text-zinc-500 hover:bg-rose-500/10 hover:text-rose-400" title="Șterge" data-testid={`import-delete-${it.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > perPage && (
        <div className="mt-3 flex items-center justify-between text-xs text-zinc-500">
          <div>Afișez {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} din {total}</div>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
              className="rounded border border-zinc-800 px-2 py-1 disabled:opacity-40">‹ Înapoi</button>
            <button onClick={() => setPage((p) => p + 1)} disabled={page * perPage >= total}
              className="rounded border border-zinc-800 px-2 py-1 disabled:opacity-40">Înainte ›</button>
          </div>
        </div>
      )}

      <ImportFormModal open={openModal} editing={editing} onClose={() => { setOpenModal(false); setEditing(null); }}
        onSaved={() => { fetchAll(); fetchStats(); }} />
    </div>
  );
}
